import { useEffect, useMemo, useState } from "react";
import { useQueryClient, useIsFetching } from "@tanstack/react-query";
import {
  useUsageSummary,
  useUsageTimeseries,
  useUsageGroup,
  useUsageStacked,
  useUsageSankeyKeys,
  useUsageSankeyModels,
} from "../hooks/useApi";
import ActivityMetricCard from "../components/ActivityMetricCard";
import ActivityTopList from "../components/ActivityTopList";
import UsageStackedChart from "../components/UsageStackedChart";
import CostDiscountAreaChart from "../components/CostDiscountAreaChart";
import AvgPriceChart from "../components/AvgPriceChart";
import SankeyChart from "../components/SankeyChart";
import Card from "../components/Card";
import { formatDollar, formatNumber, formatToman, formatTomanInt } from "../utils/format";
import {
  USAGE_DURATIONS as DURATIONS,
  USAGE_DURATION_SECONDS as DURATION_SECONDS,
  USAGE_DURATION_STORAGE_KEY as DURATION_STORAGE_KEY,
  loadUsageDuration as loadDuration,
} from "../utils/usage";

const inputClass =
  "h-9 px-3 rounded-btn border border-line text-small text-text-secondary bg-surface focus:outline-none focus:ring-2 focus:ring-primary/30";

interface UsagePoint {
  time: string;
  end: string;
  total_tokens: number;
  total_cost: number;
  calculated_cost: number;
  discount_cost: number;
  requests: number;
}

interface UsageGroupItem {
  name: string | null;
  provider: string | null;
  api_key_id: number | null;
  prefix: string | null;
  requests: number;
  input_tokens: number | string;
  output_tokens: number | string;
  total_tokens: number | string;
  cost: number | string;
}

const TOKEN_TYPE_LABELS: Record<string, string> = {
  prompt_tokens: "توکن ورودی",
  completion_tokens: "توکن خروجی",
  reasoning_tokens: "توکن استدلال",
  image_tokens: "توکن تصویر",
  audio_tokens: "توکن صدا",
  video_tokens: "توکن ویدئو",
};

const TOKEN_TYPE_COLORS: Record<string, string> = {
  prompt_tokens: "#34D399",
  completion_tokens: "#60A5FA",
  reasoning_tokens: "#A78BFA",
  image_tokens: "#FB923C",
  audio_tokens: "#F472B6",
  video_tokens: "#818CF8",
};

function formatPointTime(iso: string, duration: string): string {
  const d = new Date(iso);
  if (duration === "1y") return d.toLocaleString("fa-IR", { month: "long" });
  if (duration === "1w" || duration === "1mo")
    return d.toLocaleString("fa-IR", { day: "numeric", month: "long" });
  return d.toLocaleString("fa-IR", { hour: "2-digit", minute: "2-digit" });
}

function pctChange(cur: number, prev: number): number | null {
  return prev > 0 ? ((cur - prev) / prev) * 100 : null;
}

export default function Usage() {
  const queryClient = useQueryClient();
  const [duration, setDuration] = useState<string>(loadDuration);

  // Persist the user's preferred duration so it survives page reloads.
  useEffect(() => {
    localStorage.setItem(DURATION_STORAGE_KEY, duration);
  }, [duration]);

  const isRefreshing = useIsFetching({ queryKey: ["usage"] }) > 0;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["usage"] });
  };

  // Open-ended window: only bound the past. Omitting `to_time` lets each
  // refetch include usage created up to the current moment, so the refresh
  // button picks up brand-new requests instead of a window frozen at mount time.
  const params = useMemo(() => {
    const now = new Date();
    return { from_time: new Date(now.getTime() - DURATION_SECONDS[duration] * 1000).toISOString() };
  }, [duration]);

  const prevParams = useMemo(() => {
    const to = new Date(Date.now() - DURATION_SECONDS[duration] * 1000);
    const from = new Date(to.getTime() - DURATION_SECONDS[duration] * 1000);
    return { from_time: from.toISOString(), to_time: to.toISOString() };
  }, [duration]);

  const { data: summary, isLoading: summaryLoading } = useUsageSummary(params);
  const { data: prevSummary } = useUsageSummary(prevParams);
  const { data: seriesData, isLoading: seriesLoading } = useUsageTimeseries(duration);

  const groupParams = useMemo(
    () => ({ ...params, sort_by: "total_tokens", limit: 5 }),
    [params]
  );
  const { data: keyGroupData } = useUsageGroup("api_key", groupParams);
  const { data: modelGroupData } = useUsageGroup("model", groupParams);
  const { data: stackedData, isLoading: stackedLoading } = useUsageStacked(duration);
  const { data: keysSankeyData, isLoading: sankeyKeysLoading } = useUsageSankeyKeys(duration);
  const { data: modelsSankeyData, isLoading: sankeyModelsLoading } = useUsageSankeyModels(duration);

  const series = (seriesData || []) as UsagePoint[];

  const totalCost = Number(summary?.total_cost ?? 0);
  const prevCost = Number(prevSummary?.total_cost ?? 0);
  const totalRequests = Number(summary?.total_requests ?? 0);
  const prevRequests = Number(prevSummary?.total_requests ?? 0);
  const totalTokens = Number(summary?.total_tokens ?? 0);
  const prevTokens = Number(prevSummary?.total_tokens ?? 0);
  const totalCalc = Number(summary?.total_calculated_cost ?? 0);
  const prevCalc = Number(prevSummary?.total_calculated_cost ?? 0);

  const avgCost = totalTokens > 0 ? totalCalc / (totalTokens / 1_000_000) : 0;
  const prevAvgCost = prevTokens > 0 ? prevCalc / (prevTokens / 1_000_000) : 0;

  const spendSeries = series.map((p) => ({ time: p.time, value: Number(p.total_cost) || 0 }));
  const requestsSeries = series.map((p) => ({ time: p.time, value: Number(p.requests) || 0 }));
  const tokensSeries = series.map((p) => ({ time: p.time, value: Number(p.total_tokens) || 0 }));
  const costSeries = series.map((p) => {
    const tokens = Number(p.total_tokens) || 0;
    return {
      time: p.time,
      value: tokens > 0 ? (Number(p.calculated_cost) || 0) / (tokens / 1_000_000) : 0,
    };
  });

  const costDiscountSeries = series.map((p) => ({
    time: p.time,
    end: p.end,
    total_cost: Number(p.total_cost) || 0,
    discount_cost: Number(p.discount_cost) || 0,
  }));

  const avgPriceSeries = series.map((p) => {
    const tokens = Number(p.total_tokens) || 0;
    return {
      time: p.time,
      end: p.end,
      value: tokens > 0 ? (Number(p.calculated_cost) || 0) / (tokens / 1_000_000) : 0,
    };
  });

  const loading = summaryLoading && !summary;
  const seriesChartLoading = seriesLoading && !seriesData;

  const keyItems = ((keyGroupData || []) as UsageGroupItem[]).map((g, i) => ({
    rank: i + 1,
    name: g.name || "بدون کلید",
    subtitle: g.prefix || undefined,
    tokens: Number(g.total_tokens) || 0,
  }));

  const modelItems = ((modelGroupData || []) as UsageGroupItem[]).map((g, i) => ({
    rank: i + 1,
    name: g.name || "—",
    tokens: Number(g.total_tokens) || 0,
  }));

  return (
    <div className="space-y-6">
      {/* Page Header + Duration Filter */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-h1 text-text-main">فعالیت</h1>
          <p className="text-body text-text-secondary mt-1">مصرف دوره‌ای شما در مدل‌های مختلف</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            title="به‌روزرسانی داده‌ها"
            className="inline-flex items-center justify-center w-9 h-9 rounded-btn border border-line bg-surface text-text-secondary hover:bg-surface-hover hover:text-text-main transition-colors"
          >
            <svg
              className={isRefreshing ? "animate-spin" : ""}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 12a9 9 0 11-2.64-6.36" />
              <polyline points="21 3 21 9 15 9" />
            </svg>
          </button>
          <select
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            className={inputClass}
          >
            {DURATIONS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Metric Boxes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Box 1: Total spend */}
        <ActivityMetricCard
          label="کل هزینه"
          loading={loading}
          data={spendSeries}
          value={formatTomanInt(summary?.total_cost)}
          unit="تومان"
          changePercent={pctChange(totalCost, prevCost)}
          formatValue={(v) => formatToman(v)}
          formatTime={(iso) => formatPointTime(iso, duration)}
        />

        {/* Box 2: Requests */}
        <ActivityMetricCard
          label="درخواست‌ها"
          loading={loading}
          data={requestsSeries}
          value={formatNumber(summary?.total_requests)}
          changePercent={pctChange(totalRequests, prevRequests)}
          formatValue={(v) => formatNumber(v)}
          formatTime={(iso) => formatPointTime(iso, duration)}
        />

        {/* Box 3: Token volume */}
        <ActivityMetricCard
          label="حجم توکن"
          loading={loading}
          data={tokensSeries}
          value={formatNumber(summary?.total_tokens)}
          unit="توکن"
          changePercent={pctChange(totalTokens, prevTokens)}
          formatValue={(v) => formatNumber(v)}
          formatTime={(iso) => formatPointTime(iso, duration)}
        />

        {/* Box 4: Avg cost $/1M */}
        <ActivityMetricCard
          label={
            <>
              میانگین هزینه <span dir="ltr">$/1M</span>
            </>
          }
          loading={loading}
          data={costSeries}
          value={`$${formatDollar(avgCost)}`}
          unit="/1M"
          ltr
          changePercent={pctChange(avgCost, prevAvgCost)}
          formatValue={(v) => `$${formatDollar(v)}`}
          formatTime={(iso) => formatPointTime(iso, duration)}
        />
      </div>

      {/* Caching savings panel */}
      <Card padding="lg" className="border border-emerald-200 bg-emerald-50">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
          <div className="max-w-xl">
            <p className="text-h2 font-bold text-text-main">
              صرفه‌جویی سشن‌های طولانی و تسک‌های ایجنت‌محور
            </p>
            <p className="text-small text-text-tertiary mt-1 leading-6">
              قیمت مؤثر شما به‌صورت خودکار از کش سمت ارائه‌دهنده بهره‌مند می‌شود؛
              درصد بهره‌مندی به اشتراک شما بستگی دارد و کش برای هر درخواست تضمین‌شده نیست.
            </p>
          </div>
          <div className="flex items-center gap-8">
            <div>
              <p className="text-small text-text-tertiary mb-1">تخفیف شما</p>
              <p className="text-h1 font-bold text-emerald-600 font-number">
                {formatTomanInt(summary?.total_customer_caching_discount)}
                <span className="text-small text-text-tertiary font-normal mr-1">تومان</span>
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Top lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ActivityTopList
          title="کلیدهای API برتر"
          linkLabel="مشاهده همه ›"
          linkTo="/api-keys"
          items={keyItems}
          loading={loading}
        />
        <ActivityTopList
          title="مدل‌های برتر شما"
          linkLabel="جزئیات ›"
          linkTo="/logs"
          items={modelItems}
          loading={loading}
        />
      </div>

      {/* Full-width stacked chart */}
      <Card padding="lg">
        <h3 className="text-h3 text-text-main font-bold mb-4">مصرف به تفکیک مدل (تومان)</h3>
        <UsageStackedChart data={stackedData} duration={duration} loading={stackedLoading} />
      </Card>

      {/* Cost & savings area chart */}
      <Card padding="lg">
        <h3 className="text-h3 text-text-main font-bold mb-4">هزینه کل و تخفیف (تومان)</h3>
        <CostDiscountAreaChart data={costDiscountSeries} duration={duration} loading={seriesChartLoading} />
      </Card>

      {/* Average price per 1M tokens */}
      <Card padding="lg">
        <h3 className="text-h3 text-text-main font-bold mb-4">
          میانگین قیمت هر ۱ میلیون توکن <span dir="ltr">($/1M)</span>
        </h3>
        <AvgPriceChart data={avgPriceSeries} duration={duration} loading={seriesChartLoading} />
      </Card>

      {/* Sankey charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card padding="lg">
          <h3 className="text-h3 text-text-main font-bold mb-4">جریان مصرف کلیدها و اندپوینت‌ها</h3>
          <SankeyChart data={keysSankeyData} loading={sankeyKeysLoading} />
        </Card>
        <Card padding="lg">
          <h3 className="text-h3 text-text-main font-bold mb-4">ترکیب توکن‌ها در مدل‌های برتر</h3>
          <SankeyChart
            data={modelsSankeyData}
            loading={sankeyModelsLoading}
            rightLabels={TOKEN_TYPE_LABELS}
            rightColors={TOKEN_TYPE_COLORS}
          />
        </Card>
      </div>
    </div>
  );
}
